import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { PhotoIdentification, StockItemView } from '@almox/shared';
import { api } from '../api/client.js';
import { useAppData } from '../AppData.js';
import {
  ProductFields,
  applyVisionSuggestion,
  changedProductFields,
  needsBarcodeLink,
  productToDraft,
  type ProductDraft,
} from '../components/ProductFields.js';
import { ShareStockButton } from '../components/ShareStockButton.js';
import {
  EmptyState,
  ExpiryBadge,
  Modal,
  storageEmoji,
  useAsync,
  useToast,
} from '../components/ui.js';

/** Dispensa aberta: itens com acoes rapidas (−1, baixa total, mover, editar). */
export function StoragePage() {
  const { id = '' } = useParams();
  const { storages, refresh } = useAppData();
  const toast = useToast();
  const view = useAsync(() => api.getStorageItems(id), [id]);
  const [editing, setEditing] = useState<StockItemView | null>(null);
  const [moving, setMoving] = useState<StockItemView | null>(null);

  const storage = view.data?.storage;
  const items = view.data?.items ?? [];

  async function reload() {
    view.reload();
    await refresh();
  }

  async function consume(item: StockItemView, mode: 'decrement' | 'deplete', discarded = false) {
    try {
      const result = await api.consumeItem(item.id, {
        mode,
        quantity: 1,
        reason: discarded ? 'discarded' : 'consumed',
      });
      toast(
        result.addedToShoppingList
          ? `${item.product.name} acabou — foi para a lista de compras.`
          : `Baixa registrada em ${item.product.name}.`,
        'ok',
      );
      await reload();
    } catch (error) {
      toast((error as Error).message, 'error');
    }
  }

  if (view.loading) {
    return (
      <main className="page">
        <p className="muted">Carregando…</p>
      </main>
    );
  }

  if (view.error || !storage) {
    return (
      <main className="page">
        <EmptyState icon="🤔" title="Dispensa nao encontrada" hint={view.error ?? undefined} />
        <Link to="/" className="btn">
          Voltar
        </Link>
      </main>
    );
  }

  return (
    <main className="page">
      <div className="page-head">
        <h1>
          {storageEmoji(storage.icon)} {storage.name}
        </h1>
        <span className="badge">
          {items.length} {items.length === 1 ? 'item' : 'itens'}
        </span>
        <div className="spacer" />
        {items.length > 0 ? (
          <ShareStockButton
            storages={[storage]}
            items={items}
            title={`Almox — ${storage.name}`}
            label="📋 Copiar"
            chooser={false}
          />
        ) : null}
        <Link to={`/inventario/${storage.id}`} className="btn" title="Conferir o que esta aqui">
          🧾 Conferir
        </Link>
        <Link to="/escanear" className="btn btn-primary">
          + Dar entrada
        </Link>
      </div>

      {items.length === 0 ? (
        <EmptyState
          icon="📦"
          title="Dispensa vazia"
          hint="Escaneie um codigo de barras ou digite o codigo para guardar o primeiro item aqui."
        />
      ) : (
        <ul className="list">
          {items.map((item) => (
            <li key={item.id} className="item">
              <div className="photo">
                {item.product.photoUrl ? (
                  <img
                    src={item.product.photoUrl}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10 }}
                  />
                ) : (
                  '🥫'
                )}
              </div>
              <div className="info">
                <div className="title">{item.product.name}</div>
                <div className="sub">
                  {item.product.brand ? <span>{item.product.brand}</span> : null}
                  {item.product.size ? (
                    <span className="badge" title="Tamanho da embalagem">
                      {item.product.size}
                    </span>
                  ) : null}
                  {item.product.category ? (
                    <span className="badge" title="Classificacao do produto">
                      {item.product.category}
                    </span>
                  ) : null}
                  <ExpiryBadge item={item} />
                  {!item.restockOnDepletion ? (
                    <span className="badge" title="Nao vai para a lista de compras ao acabar">
                      sem reposicao
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="qty">
                {item.quantity} {item.product.unit}
              </div>
              <div className="actions">
                <button
                  type="button"
                  className="btn btn-sm btn-icon"
                  title="Dar baixa de 1"
                  onClick={() => consume(item, 'decrement')}
                >
                  −1
                </button>
                <button
                  type="button"
                  className="btn btn-sm"
                  title="Acabou / joguei fora"
                  onClick={() => consume(item, 'deplete', true)}
                >
                  Acabou
                </button>
                <button type="button" className="btn btn-sm" onClick={() => setMoving(item)}>
                  Mover
                </button>
                <button type="button" className="btn btn-sm" onClick={() => setEditing(item)}>
                  Editar
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing ? (
        <EditItemModal
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await reload();
          }}
        />
      ) : null}

      {moving ? (
        <Modal title={`Mover ${moving.product.name}`} onClose={() => setMoving(null)}>
          <p className="muted small">
            A transferencia nao passa pela lista de compras — o item so muda de lugar.
          </p>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}>
            {storages
              .filter((option) => option.id !== moving.storageId)
              .map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className="btn btn-block"
                  onClick={async () => {
                    try {
                      await api.transferItem(moving.id, option.id);
                      toast(`${moving.product.name} foi para ${option.name}.`, 'ok');
                      setMoving(null);
                      await reload();
                    } catch (error) {
                      toast((error as Error).message, 'error');
                    }
                  }}
                >
                  {storageEmoji(option.icon)} {option.name}
                </button>
              ))}
          </div>
        </Modal>
      ) : null}
    </main>
  );
}

function EditItemModal({
  item,
  onClose,
  onSaved,
}: {
  item: StockItemView;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [quantity, setQuantity] = useState(item.quantity);
  const [expiresAt, setExpiresAt] = useState(item.expiresAt ?? '');
  const [restock, setRestock] = useState(item.restockOnDepletion);
  const [saving, setSaving] = useState(false);
  // O produto tambem e editavel aqui, com os mesmos campos da tela "Produtos":
  // dado vindo da IA ou do catalogo publico as vezes chega errado (unidade,
  // marca, tamanho), e sem isto ficava errado para sempre.
  const [draft, setDraft] = useState<ProductDraft>(productToDraft(item.product));
  const [editandoProduto, setEditandoProduto] = useState(false);

  function aplicarFoto(result: PhotoIdentification) {
    if (result.product && result.product.id !== item.product.id) {
      toast(`Este codigo ja e do produto "${result.product.name}".`, 'error');
      return;
    }
    if (result.suggestion) {
      setDraft((atual) => applyVisionSuggestion(atual, result.suggestion!, result.barcode));
      setEditandoProduto(true);
      toast('Embalagem lida — confira os campos.', 'ok');
    }
  }

  async function save() {
    if (needsBarcodeLink(draft)) {
      toast('Informe o codigo de barras ou marque que o produto nao tem um.', 'error');
      setEditandoProduto(true);
      return;
    }
    setSaving(true);
    try {
      const patch = changedProductFields(draft, item.product);
      if (patch) await api.updateProduct(item.product.id, patch);
      await api.updateItem(item.id, {
        quantity,
        expiresAt: expiresAt ? expiresAt : null,
        restockOnDepletion: restock,
      });
      toast(quantity <= 0 ? 'Item removido da dispensa.' : 'Item atualizado.', 'ok');
      onSaved();
    } catch (error) {
      toast((error as Error).message, 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={`Editar ${item.product.name}`} onClose={onClose}>
      <div className="row">
        <div className="field">
          <label htmlFor="edit-qty">Quantidade ({draft.unit})</label>
          <input
            id="edit-qty"
            type="number"
            min={0}
            step="any"
            value={quantity}
            onChange={(event) => setQuantity(Number(event.target.value))}
          />
          <span className="small muted">Zero remove o item da dispensa.</span>
        </div>
        <div className="field">
          <label htmlFor="edit-expiry">Validade</label>
          <input
            id="edit-expiry"
            type="date"
            value={expiresAt}
            onChange={(event) => setExpiresAt(event.target.value)}
          />
          {item.expiryEstimated ? (
            <span className="small muted">Hoje e uma estimativa (~). Salvar confirma a data.</span>
          ) : null}
        </div>
      </div>
      <label className="check">
        <input
          type="checkbox"
          checked={restock}
          onChange={(event) => setRestock(event.target.checked)}
        />
        Ao acabar, mandar para a lista de compras
      </label>

      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={() => setEditandoProduto((valor) => !valor)}
      >
        {editandoProduto ? 'Menos opcoes' : 'Corrigir produto (marca, tipo, tamanho…)'}
      </button>

      {editandoProduto ? (
        <>
          <ProductFields
            value={draft}
            onChange={setDraft}
            idPrefix="edit"
            onPhotoResult={aplicarFoto}
          />
          <p className="small muted">
            Vale para o produto no catalogo, em todas as dispensas — nao so para este item.
          </p>
        </>
      ) : null}
      <div className="modal-actions">
        <button type="button" className="btn" onClick={onClose}>
          Cancelar
        </button>
        <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? 'Salvando…' : 'Salvar'}
        </button>
      </div>
    </Modal>
  );
}
